import { computed, ref } from 'vue'
import { apiFetch } from './api'
import { cache, CACHE_KEYS } from './cache'

export type AccessMode = 'full' | 'dialer_only'

const me = ref(null)

function applyDevOverride(resp: any) {
  // Manual-QA switch; statically stripped from production builds.
  if (import.meta.env.DEV && resp) {
    const forced = localStorage.getItem('raven:dev:access-mode')
    if (forced === 'dialer_only' || forced === 'full') resp.access_mode = forced
  }
  return resp
}

export async function loadCurrentUser() {
  const cached = cache.get(CACHE_KEYS.USER_ME)
  if (cached) {
    me.value = cached
    return cached
  }
  try {
    const resp = applyDevOverride(await apiFetch('/auth/me'))
    me.value = resp
    cache.set(CACHE_KEYS.USER_ME, resp)
    return resp
  } catch {
    me.value = null
    return null
  }
}

export function getCurrentUser() {
  return me.value
}

export type UserSettings = Record<string, unknown>

export async function getUserSettings(): Promise<UserSettings> {
  const resp = await apiFetch<{ user_settings: UserSettings }>('/settings/user_settings')
  return resp.user_settings
}

// Shallow merge server-side.
export async function updateUserSettings(patch: UserSettings): Promise<UserSettings> {
  const resp = await apiFetch<{ user_settings: UserSettings }>('/settings/user_settings', {
    method: 'PUT',
    body: { user_settings: patch },
    responseType: 'json',
  })
  return resp.user_settings
}

export function useCurrentUser() {
  return {
    me: computed(() => me.value),
    isAdmin: computed(() => {
      const r = String(me.value?.role || '').trim().toLowerCase()
      return r === 'owner' || r === 'admin'
    }),
    accessMode: computed<AccessMode>(() =>
      me.value?.access_mode === 'dialer_only' ? 'dialer_only' : 'full'
    ),
    isDialerOnly: computed(() => me.value?.access_mode === 'dialer_only'),
    loadCurrentUser
  }
}
